import { Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { POSITIVE_DECIMAL_REGEX } from "../../../../../constants/patterns"
import {
    TT_DEVICE_PHASES,
    TT_EVDC_MODEL_REF,
    TT_EB_TOT_INPUT_RATED_CURRENT,
    TT_MODEL,
    TT_VOLTAGE,
    TT_EB_TOT_INPUT_RATED_POWER,
    TT_EB_MAX_DEMAND_CURRENT,
    TT_EB_MAX_DEMAND_POWER,
    TT_EB_PRODUCT_CODE,
    TT_EB_FLICKER_STANDARDS,
} from "../../../../../constants/tooltips"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import ValidatedRadioGroup from "../../../../inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import ValidatedTextField from "../../../../inputs/validated/validatedTextField"
import CoreDeviceDetailsSkeleton from "../../../../loading/skeletons/coreDeviceDetailsSkeleton"
import { useFormGenerator } from "../../../../submitApplication/single/formGenerator/useFormGenerator"
import { AppCard } from "../../../../surfaces/appCard"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    EVDC_VOLTAGE_OPTIONS,
    LCT_PHASES_OPTIONS,
} from "../../formSpecs/evdcSpec"
import { EB_HIDDEN_FIELD_DEFAULTS } from "../../formSpecs/hardcodedDefaults"
import {
    HP_FLICKER_STANDARDS_REG,
    HP_FLICKER_TEST_STANDARDS,
    HP_MAX_DEMAND_CURRENT_REG,
    HP_MAX_DEMAND_POWER_REG,
    HP_MODEL_REF_REG,
    HP_MODEL_REG,
    HP_PHASES_REG,
    HP_PRODUCT_CODE,
    HP_TOT_INPUT_RATED_CURRENT_REG,
    HP_TOT_INPUT_RATED_POWER_REG,
    HP_VOLTAGE_REG,
    getHpUpdatedFormData,
    hpDeviceFormLabelMapping,
    mapHpRegisterNameToValues,
} from "../../formSpecs/hpSpec"
import { HpDeviceFormData } from "../../types/providerTypes"

interface ICoreDeviceDetailsProps {
    context: Context<IDeviceFormContextValue<HpDeviceFormData>>
}

/** Displays a form page that allows the user to enter the core
 * information about an HP device
 */
const CoreEbDeviceDetailsForm = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        formData,
        setFormData,
        errors: contextErrors,
        saveDraft,
        saveDraftLoading,
    } = useContext(context)

    const isFirstPage = currentIndex === 0

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useFormGenerator(mapHpRegisterNameToValues(formData))

    const updateCoreDeviceDetails = (): void => {
        const updatedFormData = getHpUpdatedFormData(formData, watch)
        setFormData({
            ...updatedFormData,
            ...EB_HIDDEN_FIELD_DEFAULTS,
        })
    }

    const onSaveDraft = (): void => {
        const updatedFormData = getHpUpdatedFormData(formData, watch)
        const hardcodedFormData = {
            ...updatedFormData,
            ...EB_HIDDEN_FIELD_DEFAULTS,
        }
        setFormData(hardcodedFormData)
        saveDraft(hardcodedFormData)
    }

    const onClickBack = isFirstPage
        ? undefined
        : (): void => {
              updateCoreDeviceDetails()
              setCurrentIndex(currentIndex - 1)
          }

    const onClickNext = (): void => {
        updateCoreDeviceDetails()
        setCurrentIndex(currentIndex + 1)
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    return (
        <>
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MODEL_REG}
                label={hpDeviceFormLabelMapping[HP_MODEL_REG]}
                placeholder="Enter device model..."
                tooltip={TT_MODEL}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MODEL_REF_REG}
                label={hpDeviceFormLabelMapping[HP_MODEL_REF_REG]}
                placeholder="Enter device model reference..."
                tooltip={TT_EVDC_MODEL_REF}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_PHASES_REG}
                label={hpDeviceFormLabelMapping[HP_PHASES_REG]}
                radioOptions={LCT_PHASES_OPTIONS}
                tooltip={TT_DEVICE_PHASES}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={HP_VOLTAGE_REG}
                label={hpDeviceFormLabelMapping[HP_VOLTAGE_REG]}
                options={EVDC_VOLTAGE_OPTIONS}
                tooltip={TT_VOLTAGE}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_TOT_INPUT_RATED_CURRENT_REG}
                label={hpDeviceFormLabelMapping[HP_TOT_INPUT_RATED_CURRENT_REG]}
                placeholder="Enter total input rated current..."
                tooltip={TT_EB_TOT_INPUT_RATED_CURRENT}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Total input rated current must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_TOT_INPUT_RATED_POWER_REG}
                label={hpDeviceFormLabelMapping[HP_TOT_INPUT_RATED_POWER_REG]}
                placeholder="Enter total input rated power..."
                tooltip={TT_EB_TOT_INPUT_RATED_POWER}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Total input rated power must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MAX_DEMAND_CURRENT_REG}
                label={hpDeviceFormLabelMapping[HP_MAX_DEMAND_CURRENT_REG]}
                placeholder="Enter device max current demand..."
                tooltip={TT_EB_MAX_DEMAND_CURRENT}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device max current demand must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_MAX_DEMAND_POWER_REG}
                label={hpDeviceFormLabelMapping[HP_MAX_DEMAND_POWER_REG]}
                placeholder="Enter device max demand power..."
                tooltip={TT_EB_MAX_DEMAND_POWER}
                customRules={{
                    pattern: {
                        value: POSITIVE_DECIMAL_REGEX,
                        message:
                            "Device max demand power must be a valid positive number",
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required
                registerName={HP_PRODUCT_CODE}
                label={hpDeviceFormLabelMapping[HP_PRODUCT_CODE]}
                placeholder="Enter device product code..."
                tooltip={TT_EB_PRODUCT_CODE}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={HP_FLICKER_STANDARDS_REG}
                label={hpDeviceFormLabelMapping[HP_FLICKER_STANDARDS_REG]}
                radioOptions={HP_FLICKER_TEST_STANDARDS}
                tooltip={TT_EB_FLICKER_STANDARDS}
            />
            <FormErrors
                errors={errors}
                getFriendlyName={(key: string) => hpDeviceFormLabelMapping[key]}
            />
            <FormNavigation
                onClickBack={onClickBack}
                onClickNext={() => handleSubmit(onClickNext)()}
                nextText="Next"
                onClickMiddle={onSaveDraft}
                middleDisabled={saveDraftLoading}
            />
        </>
    )
}

const CoreEbDeviceDetails = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const { formIsLoading, currentIndex } = useContext(context)

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Core Electric Boiler Details"
                    subHeader="To start, please enter the following core details about the device"
                    index={currentIndex + 1}
                />
                {formIsLoading ? (
                    <CoreDeviceDetailsSkeleton />
                ) : (
                    <CoreEbDeviceDetailsForm context={context} />
                )}
            </Stack>
        </AppCard>
    )
}

export default CoreEbDeviceDetails
